/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Badge } from '@mantine/core';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import api from '../../../utils/api';
import { downloadByteArrayAsFile } from '../../../utils/arquivo';
import { dataHoraAtual } from '../../../utils/dateUtils';
import { getCorStatusCarteiraPesca } from '../../../utils/string';
import FiltroCarteiras from '../FiltroCarteiras';
import ExportButton from '../ExportButton';
import Tabela from '../Tabela';

const TabCarteiras = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [filtro, setFiltro] = useState({
    pagina: 0,
    tamanhoPagina: 10,
    direcaoOrdenacao: 'DESC',
    campoOrdenacao: null,
    stringPesquisa: null,
    numeroCarteira: null,
    modalidadeId: null,
    statusId: null,
    dataInicioPeriodo: null,
    dataFimPeriodo: null
  });
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  const downloadCSV = () => {
    const filtroCSV = {
      ...filtro,
      tamanhoPagina: 1000000
    };
    api.post(`/api/carteira/transparencia/csv/carteira-pesca`, filtroCSV, {
      responseType: 'arraybuffer'
    }).then((response) => {
      downloadByteArrayAsFile(
        response.data,
        `Relatorio_Exportar_Carteira_Transparencia_${dataHoraAtual()}.csv`,
        'text/csv'
      );
    });
  };

  const findCarteiras = async (param) => {
    try {
      await findByFilter(param);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const findByFilter = async (param) => {
    try {
      const result = await api.post(
        '/api/carteira/transparencia/carteira-pesca/filtro',
        param
      );
      setData(result.data.content);
      setTotal(result.data.totalElements);
    } catch (error) {
      console.error('Error while filtering data:', error);
    }
  };

  const handleFilter = () => {
    findCarteiras(filtro);
  };

  const form = useForm({
    initialValues: {
      stringPesquisa: '',
      numeroCarteira: '',
      modalidadeId: null,
      statusId: [],
      dataInicio: null,
      dataFinal: null
    }
  });

  const clearFilter = () => {
    form.setValues({
      stringPesquisa: '',
      numeroCarteira: '',
      modalidadeId: null,
      statusId: [],
      dataInicio: null,
      dataFinal: null
    });

    setFiltro({
      pagina: 0,
      tamanhoPagina: 10,
      direcaoOrdenacao: 'DESC',
      campoOrdenacao: null,
      stringPesquisa: null,
      numeroCarteira: null,
      modalidadeId: null,
      statusId: null,
      dataInicioPeriodo: null,
      dataFimPeriodo: null
    });

    const localResetFilter = {
      pagina: 0,
      tamanhoPagina: 10,
      direcaoOrdenacao: 'DESC',
      campoOrdenacao: null,
      stringPesquisa: null,
      numeroCarteira: null,
      modalidadeId: null,
      statusId: null,
      dataInicioPeriodo: null,
      dataFimPeriodo: null
    };

    findCarteiras(localResetFilter);
  };

  const columnsTable = useMemo(
    () => [
      {
        accessorKey: 'protocolo',
        header: 'Nº do Protocolo',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'modalidade',
        header: 'Modalidade',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'limiteCaptura',
        header: 'Limite de Captura',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'dataCriacaoFormatado',
        header: 'Data de Criação',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'dataAtivacaoFormatado',
        header: 'Data de Ativação',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'dataVencimentoFormatado',
        header: 'Data de Vencimento',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'nomeSolicitante',
        header: 'Nome do Solicitante',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'cpfCensurado',
        header: 'CPF',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'passaporteCensurado',
        header: 'Passaporte',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'valorPagoFormatado',
        header: 'Valor Pago (R$)',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        },
        Cell: ({ cell }) => {
          return (
            <span>R$ {cell.getValue()}</span>
          );
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        },
        Cell: ({ cell }) => {
          return (
            <Badge
              color={getCorStatusCarteiraPesca(
                String(cell.getValue())
              )}
            >
              {String(cell.getValue())}
            </Badge>
          );
        }
      }
    ],
    []
  );

  useEffect(() => {
    findCarteiras(filtro);
  }, []);

  useEffect(() => {
    if (
      pagination.pageIndex !== filtro.pagina ||
      pagination.pageSize !== filtro.tamanhoPagina
    ) {
      const localFiltro = {
        ...filtro,
        tamanhoPagina: pagination.pageSize,
        pagina: pagination.pageIndex
      };
      setFiltro(localFiltro);
      findCarteiras(localFiltro);
    }
  }, [pagination]);

  useEffect(() => {
    const filtroAtualizado = {
      ...filtro,
      stringPesquisa: form.values.stringPesquisa || null,
      numeroCarteira: form.values.numeroCarteira || null,
      modalidadeId: form.values.modalidadeId || null,
      statusId: form.values.statusId || null,
      dataInicioPeriodo: form.values.dataInicio
        ? dayjs(form.values.dataInicio).format('YYYY-MM-DDTHH:mm:ss')
        : null,
      dataFimPeriodo: form.values.dataFinal
        ? dayjs(form.values.dataFinal).format('YYYY-MM-DDTHH:mm:ss')
        : null,
      campoOrdenacao: filtro.campoOrdenacao || null
    };

    setFiltro(filtroAtualizado);
  }, [form.values]);

  return (
    <Stack gap={'0'}>
      <FiltroCarteiras
        clearFilter={clearFilter}
        form={form}
        handleFilter={handleFilter}
      />
      <ExportButton onExport={downloadCSV} />
      <Tabela
        columns={columnsTable}
        data={data}
        rowCount={total}
        title={'Listagem de Carteiras de Pesca emitidas (Sistema de Carteira de Pesca)'}
        setPagination={setPagination}
        state={{
          pagination: {
            pageIndex: filtro.pagina,
            pageSize: filtro.tamanhoPagina
          }
        }}
      />
    </Stack>
  );
};

export default TabCarteiras;
